import { PathWizardData } from "./types";
import {
  ValidationResult,
  WizardStepErrors,
} from "@components/WizardCst/types";

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object" && "id" in value) {
    return !(value as { id: unknown }).id;
  }
  return false;
};

type Rule = (data: PathWizardData) => { field: string; error: string } | null;

const required =
  (key: keyof PathWizardData): Rule =>
  (d) =>
    isEmpty(d[key]) ? { field: key as string, error: "required" } : null;

const maxLength =
  (key: keyof PathWizardData, max: number): Rule =>
  (d) => {
    const v = d[key];
    return typeof v === "string" && v.length > max
      ? { field: key as string, error: "too-long" }
      : null;
  };

const RULES_BY_STEP: Record<string, Rule[]> = {
  "main-info": [
    required("title"),
    required("description"),
    maxLength("title", 70),
    maxLength("description", 256),
    maxLength("digited_code", 30),

    required("propaedeutic_logics"),

    required("completion_criterion"),
    (d) =>
      isEmpty(d.completion_value)
        ? { field: "completion_value", error: "required" }
        : null,
    (d) => {
      if (isEmpty(d.completion_value)) return null;
      const n = Number(d.completion_value);
      if (!Number.isFinite(n) || n < 0) {
        return { field: "completion_value", error: "invalid-number" };
      }
      if (
        (d.completion_criterion === "MIN_COMPLETION_PERC" ||
          d.completion_criterion === "COMPLETION_MINUTES_PERC") &&
        n > 100
      ) {
        return { field: "completion_value", error: "invalid-percentage" };
      }
      return null;
    },

    required("cover_id"),

    (d) =>
      d.schedule_publication && isEmpty(d.publish_date)
        ? { field: "publish_date", error: "required" }
        : null,
  ],

  "additional-info": [
    (d) => {
      if (isEmpty(d.credits)) return null;
      const n = Number(d.credits);
      if (Number.isFinite(n) && (n < 0 || n > 1000)) {
        return { field: "credits", error: "credits-out-of-range" };
      }
      return null;
    },

    (d) => {
      for (let i = 0; i < d.useful_links.length; i += 1) {
        const link = d.useful_links[i];
        if (isEmpty(link.url)) {
          return { field: `useful_links.${i}.url`, error: "required" };
        }
        if (isEmpty(link.url_label)) {
          return { field: `useful_links.${i}.url_label`, error: "required" };
        }
      }
      return null;
    },
  ],

  "add-children": [
    (d) =>
      d.selected_los.length === 0
        ? { field: "selected_los", error: "at-least-one-child" }
        : null,
  ],

  "tests-survey": [
    (d) =>
      d.selected_los.length === 0
        ? { field: "selected_los", error: "at-least-one-child" }
        : null,

    (d) =>
      d.test_id && !d.test_logic
        ? { field: "test_logic", error: "required" }
        : null,

    (d) => {
      if (d.test_logic !== "WITH_ZEROING") return null;
      if (isEmpty(d.max_attempt)) {
        return { field: "max_attempt", error: "required" };
      }
      const n = Number(d.max_attempt);
      if (!Number.isFinite(n) || n < 1) {
        return { field: "max_attempt", error: "invalid-number" };
      }
      return null;
    },

    (d) => {
      if (isEmpty(d.test_min_score)) return null;
      const n = Number(d.test_min_score);
      if (!Number.isFinite(n) || n < 0) {
        return { field: "test_min_score", error: "invalid-number" };
      }
      return null;
    },
  ],

  "associate-initiative": [],
  editorial: [],
};

const runStep = (stepKey: string, data: PathWizardData): ValidationResult => {
  const errors: WizardStepErrors = {};
  for (const rule of RULES_BY_STEP[stepKey] ?? []) {
    const r = rule(data);
    if (r && !errors[r.field]) errors[r.field] = r.error;
  }
  return { valid: Object.keys(errors).length === 0, errors };
};

export const validateMainInfo = (d: PathWizardData) => runStep("main-info", d);
export const validateAdditionalInfo = (d: PathWizardData) =>
  runStep("additional-info", d);
export const validateAddChildren = (d: PathWizardData) =>
  runStep("add-children", d);
export const validateTestsSurvey = (d: PathWizardData) =>
  runStep("tests-survey", d);
export const validateAssociateInitiative = (d: PathWizardData) =>
  runStep("associate-initiative", d);
export const validateEditorial = (d: PathWizardData) => runStep("editorial", d);
